import React, {Component, useState} from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {Button, Card, Form, Input, InputNumber, Icon, Upload, Alert} from 'antd';

import {adjustNegativeProductsToZero, getProduct, sellProduct} from './actions';
import {formItemLayout} from "../utils/style";
import {bindActionCreators} from "redux";
import ResponsiveTable from "../common/ResponsiveTable";
import {delay} from "../utils/time";

const FormItem = Form.Item;
const ButtonGroup = Button.Group;
const TextArea = Input.TextArea;

const Statuses = {
  Initial: 0,
  Ok: 1,
  Error: 2,
  NotFound: 3,
};

function soldProductReducer(soldProducts, upc) {
  if (!upc) return soldProducts;
  let soldProduct = soldProducts[upc];
  if (!soldProduct) {
    soldProduct = {
      status: Statuses.Initial,
      upc,
      quantity: 0
    };
  }
  soldProduct.quantity += 1;
  soldProducts[upc] = soldProduct;
  return soldProducts;
}

const indexOfOrDefault = (arr, val, def) => {
  const idx = arr.indexOf(val);
  return idx < 0 ? def : idx;
};

// eslint-disable-next-line react/prop-types
const Step1 = ({upcs, onSubmit, onCancel, onChange, setValue, error}) => {
  const [readingFile, setReadingFile] = useState(false);
  const _fileBeforeUpload = (file) => {
    if (file.type !== 'text/csv') return false;
    const reader = new FileReader();
    reader.readAsText(file);
    reader.onload = () => {
      const rows = [];
      const lines = reader.result.split('\n');
      const columns = lines[0].split(',');
      for (let i = 1; i < lines.length; i++) {
        const cells = lines[i].split(',');
        if (cells.some(x => !x)) continue;
        rows.push(cells);
      }
      const idIndex = indexOfOrDefault(columns, 'ID', 0);
      const qtyIndex = indexOfOrDefault(columns, 'Quantity Sold', 2);
      const _upcs = [];
      for (const cells of rows) {
        const upc = (cells[idIndex] || '').trim();
        if (!/^[0-9]{12,13}$/.test(upc)) continue;
        const qty = Number(cells[qtyIndex]);
        if (isNaN(qty) || qty <= 0) continue;
        for (let i = 0; i < qty; i++) _upcs.push(upc);
      }
      setValue(upcs + _upcs.join('\n') + '\n');
      setReadingFile(false);
    };
    setReadingFile(true);
    return false;
  };
  return (
    <Form onSubmit={onSubmit}>
      <FormItem
        {...formItemLayout}
        label="UPCs"
      >
        <TextArea
          rows={20}
          disabled={readingFile}
          name="upcs"
          value={upcs}
          onChange={onChange}
          aria-invalid={!!error}
          aria-errormessage={error}
        />
        <Upload beforeUpload={_fileBeforeUpload} showUploadList={false} disabled={readingFile}>
          <Button icon='plus'>Import from Sales report file</Button>
        </Upload>
      </FormItem>
      <FormItem>
        <ButtonGroup>
          <Button
            type="primary"
            htmlType="submit"
            disabled={!!error || readingFile}
          >
            Next
          </Button>
          <Button
            htmlType="button"
            onClick={onCancel}
          >
            Cancel
          </Button>
        </ButtonGroup>
      </FormItem>
    </Form>
  );
};

// eslint-disable-next-line react/prop-types
const Status = ({value}) => {
  let type, color;
  switch (value) {
    case Statuses.Ok:
      type = "check-circle";
      color = "#52c41a";
      break;
    case Statuses.NotFound:
      type = "exclamation-circle";
      color = "#fcba03";
      break;
    case Statuses.Error:
      type = "close-circle";
      color = "#ff0000";
      break;
    default:
      return '';
  }
  return <Icon type={type}
               theme="twoTone"
               twoToneColor={color} />;
};
// eslint-disable-next-line react/prop-types
const Step2 = ({soldProducts, submitting, submitted, hasErrors, onDone, onSubmit, getOnChange}) => {
  const columns = [
    {
      title: '', dataIndex: 'status', key: 'status',
      render: value => <Status value={value}/>,
    },
    {
      title: 'UPC', dataIndex: 'upc', key: 'upc',
      render: (value) => (
        <Input readOnly value={value} />
      )
    },
    {
      title: 'Quantity', dataIndex: 'quantity', key: 'quantity',
      render: (value, row, index) => (
        <InputNumber
          name={`soldProducts_${index}_quantity`}
          onChange={getOnChange(index)}
          value={value}
        />
      )
    },
    {title: '', dataIndex: 'error', key: 'error'}
  ];
  return (
    <Form onSubmit={onSubmit}>
      <ResponsiveTable columns={columns} rowKey="upc" dataSource={soldProducts} pagination={false}/>
      <FormItem>
        {submitted ? (
          <Button
            key="done"
            type="primary"
            htmlType="button"
            onClick={onDone}
            style={{marginRight: '1rem'}}
          >
            {hasErrors ? 'Ignore failed items' : 'Done'}
          </Button>
        ) : null}
        {!submitted || hasErrors ? (
          <Button
            key="submit"
            type="primary"
            htmlType="submit"
            disabled={submitting}
          >
            {hasErrors ? 'Retry failed items' : 'Submit'}
          </Button>
        ) : null}
      </FormItem>
      <div>
        <div>
          <Icon type="check-circle"
                theme="twoTone"
                twoToneColor="#52c41a" />
          &nbsp;means that the sold quantity for this UPC is recorded.
        </div>
        <div>
          <Icon type="exclamation-circle"
                theme="twoTone"
                twoToneColor="#fcba03" />
          &nbsp;means that this UPC is not in the system.
        </div>
        <div>
          <Icon type="close-circle"
                theme="twoTone"
                twoToneColor="#ff0000" />
          &nbsp;means that there was an error.
        </div>
      </div>
    </Form>
  );
};

const defaultState = Object.freeze({
  step: 1,
  upcs: '',
  soldProducts: [],
  submitting: false,
  submitted: false,
  hasErrors: false,
  cursor: 0,
  history: [''],
  badState: false,
});

class EnterSoldProductsPage extends Component {
  static propTypes = {
    history: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired,
    /* actions */
    adjustNegativeProductsToZero: PropTypes.func.isRequired,
    getProduct: PropTypes.func.isRequired,
    sellProduct: PropTypes.func.isRequired,
    /* state */
    salonId: PropTypes.string.isRequired,
  };

  state = defaultState;

  setState = (state, cb) => Component.prototype.setState.call(this, state, () => {
    console.log(this.state);
    localStorage.setItem('SoldProductsPage', JSON.stringify(this.state));
    cb && cb();
  });

  componentDidMount() {
    console.log('SoldProductsPage::componentDidMount');
    const value = localStorage.getItem('SoldProductsPage');
    if (value) {
      try {
        const state = JSON.parse(value);
        if (state.submitting) state.badState = true;
        this.setState(state);
      } catch (e) {
        //
      }
    }
  }

  componentWillUnmount() {
    console.log('SoldProductsPage::componentWillUnmount');
  }

  handleSubmit = async(e) => {
    e.preventDefault();
    if (this.state.submitting) return;
    this.setState({submitting: true});
    const {salonId} = this.props;
    let errors = 0;
    for (let i = 0; i < this.state.soldProducts.length; i++) {
      const soldProduct = this.state.soldProducts[i];
      if (soldProduct.status === Statuses.Ok) continue;
      let status, error;
      try {
        const req = {
          salonId,
          sku: soldProduct.upc.trim(),
          quantity: soldProduct.quantity,
        };
        await this.props.sellProduct(req);
        status = Statuses.Ok;
      } catch (err) {
        console.error(err);
        if (err.response && err.response.status === 404) {
          status = Statuses.NotFound;
        } else {
          errors++;
          status = Statuses.Error;
          const msg = err.response && err.response.data && err.response.data.message;
          error = typeof msg === 'string' ? msg : 'Unexpected error';
        }
      } finally {
        this.setState(prev => {
          const soldProducts = prev.soldProducts.slice();
          soldProducts[i] = { ...soldProducts[i], status, error };
          return { soldProducts };
        });
      }
    }
    try {
      await delay(500); // make sure last sold product is processed
      await this.props.adjustNegativeProductsToZero({salonId, reason: "automatic adjustment to zero"});
    } catch (e) {
      console.error(e);
    }
    this.setState({submitting: false, submitted: true, hasErrors: errors > 0});
  };

  handleStep1Submit = async(e) => {
    e.preventDefault();
    if (!this.state.upcs) return;
    const soldProductsMap = this.state.upcs.split('\n').map(x => x.trim())
      .reduce(soldProductReducer, {});
    const soldProducts = Object.keys(soldProductsMap).map(k => soldProductsMap[k]);
    this.setState({soldProducts, step: 2, cursor: 0, history: [''], upcs: ''});
  };

  handleDone = (e) => {
    e.preventDefault();
    this.setState(defaultState, () => this.props.history.replace(`/product/list`));
  };

  handleRollback = remove => (e) => {
    e.preventDefault();
    const cursor = this.state.cursor - remove;
    const history = this.state.history;
    const upcs = history[cursor];
    if (upcs === undefined) return;
    this.setState({upcs, cursor});
  };

  setUpcsValue = value => this.setState(state => {
    const trimmedValue = value.trim();
    const history = state.history.slice(state.cursor);
    if (history[0] !== trimmedValue) history.unshift(trimmedValue);
    return ({
      upcs: value,
      cursor: 0,
      history,
    });
  });

  handleUpcsChange = e => this.setUpcsValue(e.target.value);

  setQuantity = (idx, value) => this.setState(state => {
    const soldProducts = state.soldProducts.slice();
    soldProducts[idx] = {...soldProducts[idx], quantity: value};
    return {soldProducts};
  });

  getQuantityOnChange = idx => value => this.setQuantity(idx, value);

  handleResetStateClick = () => this.setState(defaultState);

  render() {
    const {step, upcs, soldProducts, done, submitting, submitted, hasErrors, badState} = this.state;
    const rollbackButton = step === 1
      ? [
        <Button key="undo" onClick={this.handleRollback(-1)} title="Undo"><Icon type="undo" /> Undo</Button>,
        <Button key="redo" onClick={this.handleRollback(1)} title="Redo"><Icon type="reload" /> Redo</Button>
      ]
      : null;
    return (
      <Card
        title="Enter Sold Products"
        extra={rollbackButton}
      >
        {badState ? (
          <Alert
            style={{marginBottom: '1rem'}}
            type="error"
            message={
              <span>
                The page was left before the process completed. Reminder: You need to wait for each line to complete,
                then a button with the label <b>Done</b> will appear and you need to click it.
                Click <a onClick={this.handleResetStateClick}>HERE</a> to reset the state.
              </span>
            }
          />
        ) : null}
        {step === 1 && <Step1
          upcs={upcs}
          onSubmit={this.handleStep1Submit}
          onCancel={this.handleDone}
          onChange={this.handleUpcsChange}
          setValue={this.setUpcsValue}
          error={""}
        />}
        {step === 2 && <Step2
          soldProducts={soldProducts}
          done={done}
          submitting={submitting}
          submitted={submitted}
          hasErrors={hasErrors}
          onDone={this.handleDone}
          onSubmit={this.handleSubmit}
          getOnChange={this.getQuantityOnChange}
        />}
      </Card>
    );
  }
}

const mapStateToProps = state => ({
  salonId: state.salon.selectedSalonId,
});

const mapDispatchToProps = dispatch => (bindActionCreators({
  adjustNegativeProductsToZero,
  getProduct,
  sellProduct,
}, dispatch));

export default connect(mapStateToProps, mapDispatchToProps)(EnterSoldProductsPage);



// WEBPACK FOOTER //
// ./src/Product/SoldProductsPage.js